import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  MultiSelect,
  Paper,
  Stack,
  Table,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconCheck,
  IconCopy,
  IconDots,
  IconExternalLink,
  IconPlus,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeletePlan, useFinalizePlan, usePlaeneList } from '@/api/usePlaene';
import { useAppStore } from '@/store/appStore';
import { LoadingState } from '@/components/LoadingState';
import { ErrorState } from '@/components/ErrorState';
import { EmptyState } from '@/components/EmptyState';
import { RoleGate } from '@/components/RoleGate';
import { ROLES, useCanWrite } from '@/auth/useRequireRole';
import { DoubleConfirmDialog } from '@/components/DoubleConfirmDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { Plan, PlanStatus } from '@/api/types';

const STATUS_OPTIONS: PlanStatus[] = ['draft', 'review', 'final', 'archived'];

function statusColor(s: PlanStatus): string {
  switch (s) {
    case 'final':
      return 'green';
    case 'review':
      return 'yellow';
    case 'archived':
      return 'gray';
    case 'draft':
    default:
      return 'blue';
  }
}

export function PlanListPage() {
  const { t } = useTranslation(['plaene', 'common']);
  const navigate = useNavigate();
  const einrichtungId = useAppStore((s) => s.activeEinrichtungId);
  const jahr = useAppStore((s) => s.activeFoerderjahr);
  const canWrite = useCanWrite();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PlanStatus[]>([]);
  const [planToDelete, setPlanToDelete] = useState<Plan | null>(null);
  const [planToFinalize, setPlanToFinalize] = useState<Plan | null>(null);
  const [dragTargetYear, setDragTargetYear] = useState<number | null>(null);

  const filters = useMemo(
    () => ({
      einrichtung_id: einrichtungId ?? undefined,
      foerderjahr: jahr,
      status: statusFilter.length > 0 ? statusFilter : undefined,
      search: search.trim() || undefined,
    }),
    [einrichtungId, jahr, statusFilter, search],
  );

  const query = usePlaeneList(filters);
  const deleteMutation = useDeletePlan();
  const finalizeMutation = useFinalizePlan();

  const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, plan: Plan) => {
    e.dataTransfer.setData('application/x-lft-plan-id', plan.id);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDropOnYear = (e: React.DragEvent<HTMLDivElement>, year: number) => {
    e.preventDefault();
    setDragTargetYear(null);
    const planId = e.dataTransfer.getData('application/x-lft-plan-id');
    if (planId) {
      navigate(`/plaene/clone?source=${planId}&targetYear=${year}`);
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>{t('plaene:title')}</Title>
        </div>
        <RoleGate role={[ROLES.ADMIN, ROLES.TRAEGER_ADMIN, ROLES.PLANER]}>
          <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/plaene/new')}>
            {t('plaene:actions.create')}
          </Button>
        </RoleGate>
      </Group>

      <Paper p="md" withBorder>
        <Group>
          <TextInput
            leftSection={<IconSearch size={14} />}
            placeholder={t('plaene:list.search')}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={280}
            aria-label={t('plaene:list.search')}
          />
          <MultiSelect
            placeholder={t('plaene:list.filterStatus')}
            data={STATUS_OPTIONS.map((s) => ({ value: s, label: t(`common:status.${s}`) }))}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as PlanStatus[])}
            w={260}
            aria-label={t('plaene:list.filterStatus')}
          />
        </Group>
      </Paper>

      {query.isLoading ? <LoadingState /> : null}
      {query.isError ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}

      {query.data && query.data.length === 0 && (
        <EmptyState
          title={t('plaene:list.empty')}
          description={t('plaene:list.emptyHint')}
          action={
            canWrite ? (
              <Button leftSection={<IconPlus size={16} />} onClick={() => navigate('/plaene/new')}>
                {t('plaene:actions.create')}
              </Button>
            ) : null
          }
        />
      )}

      {query.data && query.data.length > 0 ? (
        <Paper withBorder>
          <Table.ScrollContainer minWidth={760}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('plaene:col.name')}</Table.Th>
                  <Table.Th>{t('plaene:col.jahr')}</Table.Th>
                  <Table.Th>{t('plaene:col.status')}</Table.Th>
                  <Table.Th>{t('plaene:col.zuletzt')}</Table.Th>
                  <Table.Th>{t('plaene:col.von')}</Table.Th>
                  <Table.Th aria-label={t('plaene:col.aktionen')} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {query.data.map((plan) => (
                  <Table.Tr
                    key={plan.id}
                    draggable={canWrite}
                    onDragStart={(e) => handleDragStart(e, plan)}
                  >
                    <Table.Td>{plan.name}</Table.Td>
                    <Table.Td>{plan.foerderjahr}</Table.Td>
                    <Table.Td>
                      <Badge color={statusColor(plan.status)} variant="light">
                        {t(`common:status.${plan.status}`)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>{new Date(plan.updated_at).toLocaleDateString()}</Table.Td>
                    <Table.Td>{plan.updated_by ?? '—'}</Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <Tooltip label={t('plaene:actions.open')}>
                          <ActionIcon
                            variant="subtle"
                            onClick={() => navigate(`/plaene/${plan.id}`)}
                            aria-label={t('plaene:actions.open')}
                          >
                            <IconExternalLink size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <RoleGate role={[ROLES.ADMIN, ROLES.TRAEGER_ADMIN, ROLES.PLANER]}>
                          <Menu position="bottom-end" withArrow>
                            <Menu.Target>
                              <ActionIcon variant="subtle" aria-label={t('plaene:col.aktionen')}>
                                <IconDots size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                leftSection={<IconCopy size={14} />}
                                onClick={() => navigate(`/plaene/clone?source=${plan.id}`)}
                              >
                                {t('plaene:actions.clone')}
                              </Menu.Item>
                              <Menu.Item
                                leftSection={<IconCheck size={14} />}
                                onClick={() => setPlanToFinalize(plan)}
                                disabled={plan.status === 'final'}
                              >
                                {t('plaene:actions.finalize')}
                              </Menu.Item>
                              <Menu.Divider />
                              <Menu.Item
                                color="red"
                                leftSection={<IconTrash size={14} />}
                                onClick={() => setPlanToDelete(plan)}
                              >
                                {t('plaene:actions.delete')}
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </RoleGate>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      ) : null}

      <Group>
        {[jahr - 1, jahr, jahr + 1].map((year) => (
          <Paper
            key={year}
            withBorder
            p="md"
            onDragEnter={() => setDragTargetYear(year)}
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={() => setDragTargetYear((y) => (y === year ? null : y))}
            onDrop={(e) => handleDropOnYear(e, year)}
            style={{
              opacity: dragTargetYear === year ? 1 : 0.7,
              borderStyle: dragTargetYear === year ? 'dashed' : 'solid',
              minWidth: 160,
            }}
          >
            <Stack gap={2}>
              <Title order={5}>{year}</Title>
              <span>{t('plaene:dragDrop.hint')}</span>
            </Stack>
          </Paper>
        ))}
      </Group>

      {planToDelete ? (
        <DoubleConfirmDialog
          opened={!!planToDelete}
          expected={planToDelete.name}
          title={t('plaene:delete.title')}
          message={t('plaene:delete.message', { name: planToDelete.name })}
          onConfirm={() => {
            const target = planToDelete;
            setPlanToDelete(null);
            deleteMutation.mutate({
              id: target.id,
              confirm_name: target.name,
              reason: t('plaene:delete.reasonDefault'),
            });
          }}
          onCancel={() => setPlanToDelete(null)}
        />
      ) : null}

      {planToFinalize ? (
        <ConfirmDialog
          opened={!!planToFinalize}
          title={t('plaene:finalize.title')}
          message={t('plaene:finalize.message')}
          confirmLabel={t('plaene:finalize.confirm')}
          onConfirm={() => {
            const id = planToFinalize.id;
            setPlanToFinalize(null);
            finalizeMutation.mutate(id);
          }}
          onCancel={() => setPlanToFinalize(null)}
        />
      ) : null}
    </Stack>
  );
}
